import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Download, FileSpreadsheet, Monitor, Smartphone, Globe, MonitorSmartphone } from 'lucide-react';
import { useSelector } from 'react-redux';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/Badge';
import Loader from '@/components/Loader';
import Pagination from '@/components/Pagination';
import { attendanceService, userService } from '@/services';
import { fmtDate, fmtTime, minutesToHours, download, isLunchOver, activityColor } from '@/utils/format';

export default function AdminAttendance() {
  const token = useSelector((s) => s.auth.token);
  const [filters, setFilters] = useState({
    from: dayjs().startOf('month').format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
    user: '',
    status: '',
  });
  const [page, setPage] = useState(1);
  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => userService.list({ limit: 200 }) });
  const { data, isLoading } = useQuery({
    queryKey: ['attendance', 'admin', filters, page],
    queryFn: () => attendanceService.adminList({ ...filters, page, limit: 20 }),
  });

  const exportCSV = () => {
    const qp = new URLSearchParams(filters).toString();
    download(`reports/admin/attendance.csv?${qp}`, `attendance_${filters.from}_to_${filters.to}.csv`, token);
  };

  // Monthly grid sheet (ENTRY/EXIT per employee) for the month of the "From" date.
  const exportSheet = () => {
    const y = dayjs(filters.from).year();
    const m = dayjs(filters.from).month() + 1;
    download(
      `reports/admin/attendance.xlsx?year=${y}&month=${m}`,
      `Attendance_${dayjs(filters.from).format('MMMM_YYYY')}.xlsx`,
      token
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Attendance"
        subtitle="Team attendance across the date range"
        actions={
          <div className="flex gap-2">
            <button className="btn-primary" onClick={exportSheet} title={`Monthly grid for ${dayjs(filters.from).format('MMMM YYYY')}`}>
              <FileSpreadsheet size={14} /> Export sheet (Excel)
            </button>
            <button className="btn-secondary" onClick={exportCSV}>
              <Download size={14} /> Export CSV
            </button>
          </div>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div>
            <label className="label">From</label>
            <input type="date" className="input w-44" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
          </div>
          <div>
            <label className="label">To</label>
            <input type="date" className="input w-44" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
          </div>
          <div>
            <label className="label">Employee</label>
            <select className="input w-56" value={filters.user} onChange={(e) => setFilters((f) => ({ ...f, user: e.target.value }))}>
              <option value="">All employees</option>
              {(users.data?.items || []).map((u) => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input w-44" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              <option value="">All</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
              <option value="late">Late</option>
              <option value="half-day">Half day</option>
              <option value="leave">Leave</option>
              <option value="wfh">WFH</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <Loader />
        ) : (
          <div className="table-wrap">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th">Date</th>
                  <th className="th">Employee</th>
                  <th className="th">Check In</th>
                  <th className="th">Check Out</th>
                  <th className="th">Lunch</th>
                  <th className="th">Hours</th>
                  <th className="th">Activity</th>
                  <th className="th">Status</th>
                  <th className="th">Device</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).length === 0 && (
                  <tr><td colSpan={9} className="td text-center text-slate-500">No records found</td></tr>
                )}
                {(data?.items || []).map((r) => {
                  const sess = r.sessions?.length
                    ? r.sessions
                    : (r.checkInAt ? [{ checkInAt: r.checkInAt, checkOutAt: r.checkOutAt }] : []);
                  return (
                  <tr key={r._id}>
                    <td className="td">{fmtDate(r.date)}</td>
                    <td className="td">
                      <div className="font-medium">{r.user?.name}</div>
                      <div className="text-xs text-slate-500">{r.user?.department}</div>
                    </td>
                    <td className="td align-top">
                      {sess.length === 0 ? '—' : sess.map((s, i) => (
                        <div key={i} className="whitespace-nowrap">{fmtTime(s.checkInAt)}</div>
                      ))}
                      {sess.length > 1 && (
                        <div className="text-[10px] text-slate-400 mt-0.5">{sess.length} sessions</div>
                      )}
                    </td>
                    <td className="td align-top">
                      {sess.length === 0 ? '—' : sess.map((s, i) => (
                        <div key={i} className="whitespace-nowrap">
                          {s.checkOutAt ? fmtTime(s.checkOutAt) : <span className="text-emerald-500">ongoing</span>}
                        </div>
                      ))}
                    </td>
                    <td className="td whitespace-nowrap">
                      {r.lunchStartAt ? (
                        <span className={isLunchOver(r.lunchMinutes) ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                          {fmtTime(r.lunchStartAt)}{r.lunchEndAt ? ` – ${fmtTime(r.lunchEndAt)}` : ''}
                          {r.lunchMinutes ? <span className="text-xs"> · {r.lunchMinutes}m</span> : ''}
                          {isLunchOver(r.lunchMinutes) && (
                            <span className="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 text-[10px] font-semibold">Over 1h</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="td">{minutesToHours(r.workingMinutes)}</td>
                    <td className="td">
                      {r.activity ? (
                        <span className="inline-flex items-center gap-1.5" title={`Tracker: ${r.activity.activityPercent}% active`}>
                          <span className={`w-2 h-2 rounded-full ${activityColor(r.activity.activityPercent)}`} />
                          <span className="font-medium">{r.activity.activityPercent}%</span>
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="td"><StatusBadge status={r.isLate ? 'late' : r.status} /></td>
                    <td className="td">
                      <DeviceInfo meta={r.checkInMeta} />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} limit={20} total={data?.total || 0} onChange={setPage} />
      </Card>
    </div>
  );
}

// Renders the check-in device: browser, OS, mobile/desktop and IP — with icons.
function DeviceInfo({ meta }) {
  if (!meta || (!meta.browser && !meta.os && !meta.ip)) {
    return <span className="text-slate-400">—</span>;
  }
  const isMobile = meta.device && meta.device !== 'Desktop';
  const DeviceIcon = isMobile ? Smartphone : Monitor;
  return (
    <div className="text-xs text-slate-500 space-y-0.5 min-w-[150px]">
      {(meta.browser || meta.os) && (
        <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-200 font-medium">
          <DeviceIcon size={13} className="shrink-0 text-slate-400" />
          <span>{meta.browser || 'Unknown browser'}</span>
        </div>
      )}
      {meta.os && (
        <div className="pl-[19px] flex items-center gap-1.5">
          <span>{meta.os}</span>
          <span
            className={`px-1.5 py-px rounded text-[10px] font-medium inline-flex items-center gap-0.5 ${
              isMobile
                ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
            }`}
          >
            <MonitorSmartphone size={9} /> {isMobile ? 'Mobile' : 'Desktop'}
          </span>
        </div>
      )}
      {isMobile && meta.device && (
        <div className="pl-[19px] text-slate-400 truncate" title={meta.device}>{meta.device}</div>
      )}
      {meta.ip && (
        <div className="flex items-center gap-1.5">
          <Globe size={12} className="shrink-0 text-slate-400" />
          <span className="font-mono">{meta.ip}</span>
        </div>
      )}
    </div>
  );
}
